import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import { getFooter } from "@/lib/api";

// Module-level cache so the footer fetches once across route changes.
let _footerCache = null;

function XIcon({ size = 16 }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function Social({ href, label, children }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`footer-social-${label.toLowerCase()}`}
      className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-200/70 text-zinc-600 hover:bg-violet-600 hover:text-white transition-colors"
    >
      {children}
    </a>
  );
}

export default function SiteFooter() {
  const [data, setData] = useState(_footerCache);

  useEffect(() => {
    if (_footerCache) return;
    let alive = true;
    getFooter()
      .then((res) => {
        _footerCache = res;
        if (alive) setData(res);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const links = data?.links || [];
  const brand = data?.brand || {};

  return (
    <footer
      className="w-full border-t border-zinc-200 bg-zinc-50 mt-auto"
      data-testid="site-footer"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5">
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
          {/* Nav links — single line */}
          <nav
            className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-x-5 gap-y-1 whitespace-nowrap max-w-full"
            data-testid="footer-nav"
          >
            {links.map((l) =>
              l.external ? (
                <a
                  key={l.slug}
                  href={l.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-sm text-zinc-600 hover:text-violet-700 transition-colors"
                  data-testid={`footer-link-${l.slug}`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.slug}
                  to={l.href}
                  className="shrink-0 text-sm text-zinc-600 hover:text-violet-700 transition-colors"
                  data-testid={`footer-link-${l.slug}`}
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/text-alerts"
              className="shrink-0 text-sm text-zinc-600 hover:text-violet-700 transition-colors"
              data-testid="footer-link-text-alerts"
            >
              Text Alerts
            </Link>
          </nav>

          {/* Follow Us + socials */}
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-sm text-zinc-500">Follow Us</span>
            <div className="flex items-center gap-2">
              <Social href={brand.facebook} label="Facebook">
                <Facebook size={16} />
              </Social>
              <Social href={brand.instagram} label="Instagram">
                <Instagram size={16} />
              </Social>
              <Social href={brand.twitter} label="X">
                <XIcon size={15} />
              </Social>
              <Social href={brand.youtube} label="YouTube">
                <Youtube size={17} />
              </Social>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-200/70 py-4 px-5 text-center">
        <p className="text-xs text-zinc-500">
          © {new Date().getFullYear()} Mindful Minerals. All rights reserved.
          {" | "}Powered by{" "}
          <a
            href="https://postcue.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
            data-testid="footer-postcue-link"
          >
            Postcue.com™
          </a>{" "}
          AI Technologies
        </p>
      </div>
    </footer>
  );
}
